import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLine } from "../Reveal";
import { PROFILE } from "../../lib/content";
import { scrollToId } from "../Navbar";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16" data-testid="hero-section">
      {/* background layers */}
      <div className="absolute inset-0 radial-spotlight" />
      <motion.div
        style={{ y: yImg }}
        className="absolute -top-40 -right-40 w-[60vw] h-[60vw] rounded-full opacity-[0.10] blur-3xl"
      >
        <div className="w-full h-full rounded-full bg-[radial-gradient(circle,#D4AF37,transparent_60%)]" />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
        {/* text */}
        <motion.div style={{ y: yText }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-12 bg-gold" />
            <span className="text-xs uppercase tracking-[0.25em] text-gold">{PROFILE.role}</span>
          </motion.div>

          <h1 className="font-serif font-light leading-[0.92] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-foreground">
            <MaskLine delay={0.25}>Bridging</MaskLine>
            <MaskLine delay={0.4} className="text-gradient-gold italic">Healthcare</MaskLine>
            <MaskLine delay={0.55}>and</MaskLine>
            <MaskLine delay={0.7} className="text-gradient-royal italic">Innovation.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-xl text-base md:text-lg text-muted-foreground font-light leading-relaxed"
          >
            {PROFILE.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/book"
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gold text-ink text-sm uppercase tracking-[0.12em] font-semibold hover:bg-gold-light transition-colors duration-300"
            >
              Book a Consultation
              <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <button
              onClick={() => scrollToId("expertise")}
              data-testid="hero-explore-btn"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-gold/30 text-gold text-sm uppercase tracking-[0.12em] font-medium hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              Explore Expertise
            </button>
          </motion.div>
        </motion.div>

        {/* portrait */}
        <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center">
          <motion.div
            style={{ y: yImg, opacity }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[50%_50%_6px_6px/42%_42%_6px_6px] bg-[radial-gradient(circle,rgba(212,175,55,0.35),transparent_70%)] blur-2xl" />
            <div className="relative w-[280px] sm:w-[340px] md:w-[400px] aspect-[4/5] arch-mask overflow-hidden border border-gold/25 gold-glow bg-surface">
              <img
                src={PROFILE.primaryPhoto}
                alt={PROFILE.name}
                className="w-full h-full object-cover object-top scale-[1.14] origin-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollToId("marquee")}
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground hover:text-gold transition-colors"
        aria-label="Scroll down"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
